import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

// --- DTOs ---

export interface WebhookLog {
  id: number;
  timestamp: string;
  method: string;
  path: string;
  statusCode: number;
  responseTimeMs: number;
  source: string;
  requestBody?: string;
  responseBody?: string;
  errorMessage?: string;
  uniqueTrackingId?: string;
}

export interface PagedWebhookLogs {
  items: WebhookLog[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface IntegrationHealth {
  source: string;
  lastWebhookTimestamp?: string;
  successRate24h: number;
  avgResponseTimeMs: number;
  totalRequests24h: number;
  status: 'healthy' | 'degraded' | 'unhealthy';
}

export interface TimelineEvent {
  timestamp: string;
  eventType: string;
  source: string;
  details: string;
  metadata?: Record<string, unknown>;
}

export interface ErrorPattern {
  endpoint: string;
  statusCode: number;
  count: number;
  lastOccurrence: string;
  sampleMessage?: string;
}

export interface RequestVolumePoint {
  timestamp: string;
  count: number;
  successCount: number;
  errorCount: number;
}

export interface ApiResponse<T> {
  retcode: number;
  message: string;
  data?: T;
}

@Injectable({
  providedIn: 'root',
})
export class IntegrationMonitorService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/monitoring/integrations`;

  getWebhookLogs(
    page: number,
    pageSize: number,
    source?: string,
    status?: string,
    from?: string,
    to?: string
  ): Observable<PagedWebhookLogs> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    if (source) params = params.set('source', source);
    if (status) params = params.set('status', status);
    if (from) params = params.set('from', from);
    if (to) params = params.set('to', to);

    return this.http
      .get<ApiResponse<PagedWebhookLogs>>(`${this.baseUrl}/webhook-logs`, { params })
      .pipe(
        map(r => r.data ?? { items: [], totalCount: 0, page, pageSize })
      );
  }

  getWebhookLogDetail(id: number): Observable<WebhookLog> {
    return this.http
      .get<ApiResponse<WebhookLog>>(`${this.baseUrl}/webhook-logs/${id}`)
      .pipe(
        map(r => {
          if (!r.data) throw new Error('Log not found');
          return r.data;
        })
      );
  }

  getHealthSummary(): Observable<IntegrationHealth[]> {
    return this.http
      .get<ApiResponse<IntegrationHealth[]>>(`${this.baseUrl}/health-summary`)
      .pipe(map(r => r.data ?? []));
  }

  getOrderTimeline(uniqueId: string): Observable<TimelineEvent[]> {
    return this.http
      .get<ApiResponse<TimelineEvent[]>>(`${this.baseUrl}/order-timeline/${encodeURIComponent(uniqueId)}`)
      .pipe(map(r => r.data ?? []));
  }

  getErrorPatterns(from?: string, to?: string): Observable<ErrorPattern[]> {
    let params = new HttpParams();
    if (from) params = params.set('from', from);
    if (to) params = params.set('to', to);

    return this.http
      .get<ApiResponse<ErrorPattern[]>>(`${this.baseUrl}/error-patterns`, { params })
      .pipe(map(r => r.data ?? []));
  }

  getRequestVolume(
    granularity: 'hourly' | 'daily',
    from?: string,
    to?: string
  ): Observable<RequestVolumePoint[]> {
    let params = new HttpParams().set('granularity', granularity);
    if (from) params = params.set('from', from);
    if (to) params = params.set('to', to);

    return this.http
      .get<ApiResponse<RequestVolumePoint[]>>(`${this.baseUrl}/request-volume`, { params })
      .pipe(map(r => r.data ?? []));
  }
}
